/**
 * GS_CUSTOM: storefront custom event publisher.
 *
 * Publishes events for bespoke UI interactions that Shopify's standard customer
 * events cannot see. It sends nothing to any third party: every event goes to
 * Shopify's own Customer Events bus via `Shopify.analytics.publish`. A custom
 * web pixel (docs/gs-custom-events-pixel.js) subscribes and is the only place
 * that forwards anything off-site, and the only place consent is enforced.
 *
 * Design notes
 *   - Everything here is a document-level listener or observer. No vendor file
 *     and no feature file is modified, so Horizon updates never conflict with
 *     the instrumentation, and deleting this asset removes all of it.
 *   - Every publish is wrapped in try/catch. Instrumentation must never break a
 *     page; a thrown error here would be a worse outcome than a missing event.
 *   - Events are de-duplicated so hover- and mutation-driven sources cannot
 *     flood the pixel. See once() and the dwell handling below.
 *   - No PII: product handles, variant ids, market handle and a customer *tier*
 *     only. Never an email, name, address or order id.
 *
 * Event names are prefixed `gs:` per Shopify guidance on avoiding collisions
 * with standard events. The pixel maps them to GA4 names without the prefix,
 * because GA4 event names may not contain a colon.
 */
(function () {
  'use strict';

  if (window.__gsAnalyticsEvents) return;
  window.__gsAnalyticsEvents = true;

  var NS = 'gs:';

  /* ── Context published with every event ─────────────────────────────────── */

  var CTX = (function () {
    try {
      var el = document.getElementById('gs-analytics-context');
      return el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      return {};
    }
  })();

  /* Fires once per key, per page view. */
  var FIRED = Object.create(null);

  function once(key) {
    if (FIRED[key]) return false;
    FIRED[key] = true;
    return true;
  }

  function publish(name, payload) {
    try {
      var S = window.Shopify;
      if (!S || !S.analytics || typeof S.analytics.publish !== 'function') return;

      var body = {
        market: CTX.market || '',
        market_country: CTX.market_country || '',
        template: CTX.template || ''
      };
      for (var k in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, k)) body[k] = payload[k];
      }

      S.analytics.publish(NS + name, body);
    } catch (e) {
      /* never let instrumentation break the page */
    }
  }

  /**
   * The variant selected *now*, not the one Liquid rendered. The product form's
   * id input is the live source of truth after a variant switch.
   */
  function liveVariantId() {
    try {
      var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      var v = input && input.value ? parseInt(input.value, 10) : 0;
      return isNaN(v) ? 0 : v;
    } catch (e) {
      return 0;
    }
  }

  function handleFromHref(href) {
    if (!href) return '';
    return String(href).split('?')[0].split('#')[0].split('/products/').pop().split('/')[0];
  }

  /* ── 1 + 2. Size chart: opened, empty, unit toggled ─────────────────────── */

  /* popup-link is a generic block also used for care and shipping notes, so
     only popups that look like a size chart are of interest. */
  var SIZE_HINT = /size|fit|measure|sizing/i;

  function describeChart(dialog) {
    var inner = dialog.querySelector('.popup-link__inner') || dialog;
    var table = inner.querySelector('table');
    var heading = '';
    var h = dialog.querySelector('h2');
    if (h) heading = (h.textContent || '').trim().slice(0, 80);

    var pressed = dialog.querySelector('.gs-unit-toggle__btn[aria-pressed="true"]');

    return {
      heading: heading,
      has_table: !!table,
      row_count: table ? table.querySelectorAll('tr').length : 0,
      source_unit: (pressed && pressed.dataset.unit) || ''
    };
  }

  function onChartOpen(dialog) {
    var info = describeChart(dialog);

    /* Match on the heading as well as the body: a size chart that failed to
       load has no table and no measurements, but the block heading still says
       "Size Chart" — which is exactly the case worth catching. */
    var text = (info.heading + ' ' + (dialog.textContent || '')).slice(0, 400);
    if (!SIZE_HINT.test(text)) return;

    var payload = {
      product_id: CTX.product_id || 0,
      product_handle: CTX.product_handle || '',
      variant_id: liveVariantId() || CTX.variant_id || 0,
      heading: info.heading,
      has_table: info.has_table,
      row_count: info.row_count,
      source_unit: info.source_unit
    };

    publish('size_chart_opened', payload);

    /* A silent production failure, not a behaviour metric: the button works and
       the dialog opens, but there is no chart inside — an unassigned chart, or
       a page reference that failed to resolve and left Shopify's placeholder.
       Any volume here is a defect to fix, not a number to report. */
    if (!info.has_table || info.row_count < 2) {
      publish('size_chart_empty', payload);
    }
  }

  /* Horizon opens the dialog declaratively (on:click="/showDialog"), so there
     is no callback of ours to hook. Watching the `open` attribute is true
     regardless of how it was opened. */
  function watchDialog(dialog) {
    if (dialog.dataset.gsWatched === '1') return;
    dialog.dataset.gsWatched = '1';

    var pending = null;

    var fire = function () {
      clearTimeout(pending);
      /* the size-chart enhancer builds the table on open; let it finish */
      pending = setTimeout(function () {
        if (dialog.open) onChartOpen(dialog);
      }, 200);
    };

    new MutationObserver(function () {
      if (dialog.open) fire();
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

    if (dialog.open) fire();
  }

  function scanDialogs() {
    document.querySelectorAll('dialog.popup-link__content').forEach(watchDialog);
  }

  /* Unit toggle: which unit the shopper switched *to* answers whether
     international shoppers should be given inches by default.

     Capture phase, and it has to be: gs-size-chart-enhancer binds its handler
     directly on the button, so by the time a bubble-phase listener on document
     runs, aria-pressed has already moved to the new unit — `from` would read
     back as `to` and every toggle would be discarded as a no-op. */
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('.gs-unit-toggle__btn') : null;
      if (!btn) return;

      var to = btn.dataset.unit || '';
      var group = btn.closest('.gs-unit-toggle');
      var current = group && group.querySelector('.gs-unit-toggle__btn[aria-pressed="true"]');
      var from = (current && current.dataset.unit) || '';

      if (!to || from === to) return;

      publish('size_chart_unit_toggled', {
        from: from,
        to: to,
        product_handle: CTX.product_handle || ''
      });
    },
    true
  );

  /* ── 3. Sibling colourway previewed ─────────────────────────────────────── */

  /* Hover is noisy: a pointer crossing a swatch row would emit an event per
     swatch. Require a real dwell, de-duplicate each from/to pair per page, and
     cap the total so one restless pointer cannot dominate the dataset. */
  var DWELL_MS = 500;
  var MAX_PREVIEWS = 25;
  var previewCount = 0;
  var dwellTimer = null;
  var originalHandle = new WeakMap();

  var SWATCH = '.gs-sibling-swatches-card a[data-gs-sibling-title]';
  var CARD = 'product-card, .product-card';

  /* Capture phase: gs-card-swatch-hover.js listens on the bubble phase and
     rewrites the card links to the previewed sibling. Reading the card's own
     handle here, before that runs, is the only way to get a truthful `from`. */
  document.addEventListener(
    'mouseover',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var link = t.closest(SWATCH);
      if (!link) return;
      var card = link.closest(CARD);
      if (!card) return;

      if (!originalHandle.has(card)) {
        var own = card.querySelector('[ref="productCardLink"], [ref="productTitleLink"]');
        originalHandle.set(card, handleFromHref(own && own.getAttribute('href')));
      }

      var from = originalHandle.get(card) || '';
      var to = handleFromHref(link.getAttribute('href'));
      if (!to || !from || from === to) return;

      clearTimeout(dwellTimer);
      dwellTimer = setTimeout(function () {
        if (previewCount >= MAX_PREVIEWS) return;
        if (!once('preview:' + from + '>' + to)) return;
        previewCount++;
        publish('sibling_colourway_previewed', { from_product: from, to_product: to });
      }, DWELL_MS);
    },
    true
  );

  document.addEventListener(
    'mouseout',
    function (e) {
      var t = e.target;
      if (t && t.closest && t.closest(SWATCH)) clearTimeout(dwellTimer);
    },
    true
  );

  /* ── 4. Video sound unmuted ─────────────────────────────────────────────── */

  /* Only a deliberate unmute counts. media.js also re-mutes automatically when
     a video scrolls out of view, and that says nothing about whether the toggle
     earns its place. Reading isMuted after the click has been handled tells us
     which direction the shopper actually went. */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[ref="toggleSoundButton"]') : null;
    if (!btn) return;

    var host = btn.closest('deferred-media');
    if (!host) return;

    setTimeout(function () {
      if (host.isMuted !== false) return;
      var iframe = host.querySelector('iframe[data-video-type]');
      publish('video_sound_unmuted', {
        product_handle: CTX.product_handle || '',
        media_type: iframe ? iframe.dataset.videoType || 'embed' : 'native'
      });
    }, 0);
  });

  /* ── 5. Cart agreement shown / accepted ─────────────────────────────────── */

  /* Which gate a shopper met is derivable from the hidden cart-attribute inputs
     the notice already renders, so no theme markup change is needed. */
  var REASON_BY_ATTRIBUTE = {
    'attributes[Delayed Shipping Items]': 'shipping',
    'attributes[Final Sale Items]': 'final_sale',
    'attributes[Pre-Order Items]': 'preorder'
  };

  function reasonsFor(notice) {
    var reasons = [];
    notice.querySelectorAll('.cart-agreements-attribute').forEach(function (input) {
      var reason = REASON_BY_ATTRIBUTE[input.getAttribute('name')];
      if (reason && reasons.indexOf(reason) === -1) reasons.push(reason);
    });
    return reasons.sort().join('+') || 'unknown';
  }

  function checkAgreementsShown() {
    document.querySelectorAll('.cart-agreements-notice').forEach(function (notice) {
      if (notice.hidden) return;
      var reason = reasonsFor(notice);
      if (!once('agreement_shown:' + reason)) return;
      publish('cart_agreement_shown', { reason: reason });
    });
  }

  document.addEventListener('change', function (e) {
    var box = e.target;
    if (!box || !box.classList || !box.classList.contains('cart-agreements-checkbox')) return;
    if (!box.checked) return;

    var notice = box.closest('.cart-agreements-notice');
    if (!notice) return;

    var reason = reasonsFor(notice);
    if (!once('agreement_accepted:' + reason)) return;
    publish('cart_agreement_accepted', { reason: reason });
  });

  /* ── 6. Variant unavailable selected ────────────────────────────────────── */

  /* Horizon leaves sold-out option values clickable and marks them with
     data-option-available="false", so a shopper really can select a size that
     cannot be bought. That click is unmet demand at size level — the number
     behind "size 2 runs hot" — and nothing in the standard event set sees it.

     De-duplicated per variant per page: flicking between sizes to see what is
     left is one shopper looking, not several signals. */
  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || input.type !== 'radio') return;
    if (input.dataset.optionAvailable !== 'false') return;

    var variantId = parseInt(input.dataset.variantId || '0', 10) || 0;
    if (!once('unavailable:' + (variantId || input.value))) return;

    publish('variant_unavailable_selected', {
      product_handle: CTX.product_handle || '',
      option_name: input.dataset.optionName || '',
      option_value: input.value || '',
      variant_id: variantId
    });
  });

  /* ── 7. Back in stock ───────────────────────────────────────────────────── */

  /* The form is Klaviyo's, injected at runtime — it is not in the theme at all,
     so it can only be found in the live DOM. Klaviyo already records the signup;
     what it cannot give you is the GA4 session, so this is what joins "wanted a
     sold-out size" to the campaign that paid for the visit.

     Anchored on the #klaviyo-bis-button-container id. Klaviyo's own class names
     are hashed (go2259797205) and change between their releases, so matching on
     those would break silently one morning. */
  var bisContext = null;

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest('#klaviyo-bis-button-container')) return;

    var variantId = liveVariantId() || CTX.variant_id || 0;
    var selected = document.querySelector('input[type="radio"][data-option-available]:checked');

    bisContext = {
      product_handle: CTX.product_handle || '',
      variant_id: variantId,
      option_name: (selected && selected.dataset.optionName) || '',
      option_value: (selected && selected.value) || '',
      at: Date.now()
    };

    if (!once('bis_opened:' + variantId)) return;
    publish('back_in_stock_opened', {
      product_handle: bisContext.product_handle,
      variant_id: bisContext.variant_id,
      option_name: bisContext.option_name,
      option_value: bisContext.option_value
    });
  });

  /* Klaviyo renders the form into the page rather than an iframe, so the submit
     is reachable. It is gated on a BIS button having been clicked here first:
     `form.klaviyo-form` is also Klaviyo's newsletter popup, and counting those
     as back-in-stock demand would quietly inflate exactly the number this
     exists to measure. */
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || !form.classList || !form.classList.contains('klaviyo-form')) return;
      if (!bisContext || Date.now() - bisContext.at > 600000) return;

      var payload = {
        product_handle: bisContext.product_handle,
        variant_id: bisContext.variant_id,
        option_name: bisContext.option_name,
        option_value: bisContext.option_value,
        /* carried so a drift in which Klaviyo form serves BIS shows up in the
           data, rather than as the event quietly stopping */
        form_id: (form.dataset.testid || '').slice(0, 60)
      };
      bisContext = null;

      publish('back_in_stock_submitted', payload);
    },
    true
  );

  /* ── 8. Search that found nothing ───────────────────────────────────────── */

  /* Enhanced measurement already reports view_search_results with the term, but
     never says whether anything came back. The count comes from Liquid because
     the results page renders a fallback collection when there are no matches,
     so the DOM looks identical either way. */
  function checkSearchNoResults() {
    if (!CTX.search_performed) return;
    if (CTX.search_results_count !== 0) return;
    if (!once('search_no_results')) return;

    var term = String(CTX.search_term || '').trim();
    /* A search box is a free text field and people paste odd things into it.
       An address is the one shape worth refusing outright. */
    if (!term || term.indexOf('@') !== -1) return;

    publish('search_no_results', { search_term: term.slice(0, 100) });
  }

  /* ── 9. Launch gate viewed ──────────────────────────────────────────────── */

  function checkLaunchGate() {
    var wrapper = document.getElementById('LaunchWrapper');
    if (!wrapper) return;
    if (!once('launch_gate')) return;

    publish('launch_gate_viewed', {
      tier: wrapper.dataset.customerTier || 'public',
      product_handle: CTX.product_handle || '',
      logged_in: wrapper.dataset.customerLoggedIn === 'true'
    });
  }

  /* ── Wiring ─────────────────────────────────────────────────────────────── */

  function scan() {
    scanDialogs();
    checkAgreementsShown();
    checkLaunchGate();
    checkSearchNoResults();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  /* The cart drawer, quick-add and variant switches inject markup after load,
     so re-scan when the DOM settles. Debounced: morphSection can produce a
     burst of mutations and a re-scan per mutation would be wasteful. */
  var rescan = null;
  new MutationObserver(function () {
    clearTimeout(rescan);
    rescan = setTimeout(scan, 150);
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('cart:update', function () {
    setTimeout(scan, 150);
  });
})();
